// HowToTab — step-by-step processen för att generera flows med en
// extern AI. Extraherad ur AiJsonDialog (#4 slice 3).

export function HowToTab() {
  return (
    <div className="mt-0 space-y-4 pr-4">
      <h3 className="text-lg font-semibold">How to Generate Flows with AI</h3>
      <p className="text-muted-foreground">
        Use any large language model to generate workflow JSON that imports directly into EngiCoro Flow Weaver. Here's the step-by-step process:
      </p>

      <div className="space-y-3">
        <div className="p-4 rounded-lg bg-muted border border-border/50">
          <h4 className="font-medium text-sm mb-1.5">Step 1: Copy the System Prompt</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Go to the "AI Prompt" tab and copy the system prompt. This contains the full JSON schema, rules, and constraints that the AI needs to produce valid output.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted border border-border/50">
          <h4 className="font-medium text-sm mb-1.5">Step 2: Open Your Preferred AI</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Works with ChatGPT, Claude, Gemini, Grok, local models (Ollama, LM Studio), or any OpenAI-compatible API. Paste the system prompt as the first message or system instructions.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted border border-border/50">
          <h4 className="font-medium text-sm mb-1.5">Step 3: Describe Your Workflow</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Tell the AI what you want to visualize. Be specific about layers, groups, block types, and connections. See the "Examples" tab for ready-to-use prompts you can adapt.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted border border-border/50">
          <h4 className="font-medium text-sm mb-1.5">Step 4: Import the JSON</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Copy the AI's JSON output. In Flow Weaver, use File menu → Import JSON, or paste and use Copy JSON. The app accepts both compact and verbose key formats. Importing appends to the canvas — your existing work is preserved.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted border border-border/50">
          <h4 className="font-medium text-sm mb-1.5">Export modes</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            "Copy JSON" / "Download JSON" opens an export dialog with an <strong>Export all</strong> checkbox (off by default). Off → basic data: structure + short block summaries (<code className="text-[11px]">description</code>). On → also includes <code className="text-[11px]">detailedDescription</code> (markdown from the Detail dialog), icons, subjects, to-fields and connector labels. Toggle on only when you intend to share the rich content.
          </p>
        </div>
        <div className="p-4 rounded-lg bg-muted border border-border/50">
          <h4 className="font-medium text-sm mb-1.5">Step 5: Refine Visually</h4>
          <p className="text-sm text-muted-foreground leading-relaxed">
            After import, drag blocks to fine-tune positions, edit labels, adjust connector routing, change colors, and add/remove blocks. The AI gives you a strong starting point; you polish the details.
          </p>
        </div>
      </div>

      <div className="p-3 rounded-lg bg-accent/10 border border-accent/20 text-sm">
        <strong>Tip:</strong> You can also attach images (architecture diagrams, whiteboards, screenshots) to multimodal AI models — they can convert visual diagrams directly into Flow Weaver JSON.
      </div>

      <div>
        <h4 className="font-medium text-sm mb-3">What You Can Build</h4>
        <ul className="space-y-2 text-sm">
          <li className="p-3 rounded-lg bg-muted flex items-start gap-2">
            <span className="text-primary font-bold text-base leading-none mt-0.5">*</span>
            <span><strong>IT processes</strong> — onboarding, offboarding, incident response, change management</span>
          </li>
          <li className="p-3 rounded-lg bg-muted flex items-start gap-2">
            <span className="text-primary font-bold text-base leading-none mt-0.5">*</span>
            <span><strong>Software architecture</strong> — microservices, CI/CD pipelines, data flows, API gateways</span>
          </li>
          <li className="p-3 rounded-lg bg-muted flex items-start gap-2">
            <span className="text-primary font-bold text-base leading-none mt-0.5">*</span>
            <span><strong>Business processes</strong> — approval chains, customer journeys, support escalation</span>
          </li>
          <li className="p-3 rounded-lg bg-muted flex items-start gap-2">
            <span className="text-primary font-bold text-base leading-none mt-0.5">*</span>
            <span><strong>Organizational maps</strong> — team structures, responsibility matrices, project hierarchies</span>
          </li>
          <li className="p-3 rounded-lg bg-muted flex items-start gap-2">
            <span className="text-primary font-bold text-base leading-none mt-0.5">*</span>
            <span><strong>Network/infrastructure</strong> — server topology, security zones, cloud architecture</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
